<template>
  <div>
    <slot name="reference"></slot>
    <transition name="fade-transition" mode="out-in" @after-leave="onDestroy">
      <span v-show="isVisible" :class="['c-tooltip', staticClass]">
        <slot>
          <div class="py-2 px-3">
            {{ content }}
          </div>
        </slot>

        <div class="c-tooltip__arrow" data-popper-arrow />
      </span>
    </transition>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'

import { event, isTouchDevice } from '@/mixins/helpers'

const placements = ['auto', 'auto-start', 'auto-end', 'top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end']

const DefaultTrigger = 'hover'

export default {
  name: 'BaseTooltip',

  props: {
    value: {
      type: Boolean,
      defautl: false
    },
    placement: {
      type: String,
      default: 'top',
      validator: value => placements.includes(value)
    },
    trigger: {
      type: String,
      default: DefaultTrigger,
      validator: value => ['click', 'toggle', 'hover', 'focus'].includes(value)
    },
    content: {
      type: String,
      default: ''
    },
    appendToBody: {
      type: Boolean,
      default: true
    },
    staticClass: {
      type: String,
      required: false
    }
  },

  mixins: [ event, isTouchDevice ],

  data () {
    return {
      localValue: this.value,
      isAppendedToBody: false,

      popperInstance: null,
      tooltip: null
    }
  },

  created () {
    if (this.trigger === DefaultTrigger && this.isTouchDevice) {
      this.trigger = 'focus'
    }
  },

  mounted () {
    this.tooltip = this.$el.querySelector('.c-tooltip')

    this.$watch(() => this.value, value => {
      if (this.localValue !== value) {
        value ? this.onShow() : this.onHide()
      }
    }, { immediate: true })

    this.$nextTick(() => {
      this.setEventListeners(true)
    })
  },

  beforeDestroy () {
    this.setEventListeners(false)

    this.onDestroy()
  },

  computed: {
    isVisible: {
      get () {
        return this.localValue
      },
      set (value) {
        this.localValue = value
        this.$emit('input', value)
      }
    }
  },

  methods: {
    onShow () {
      if (this.popperInstance) {
        this.popperInstance.destroy()
      }

      if (this.appendToBody && !this.isAppendedToBody) {
        this.isAppendedToBody = true
        document.body.appendChild(this.tooltip)
      }

      this.isVisible = true

      this.popperInstance = createPopper(this.$slots.reference[0].elm, this.tooltip, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8]
            }
          }],
        onFirstUpdate: () => {
          this.$nextTick(() => {
            this.popperInstance.update()
          })
        }
      })
    },

    onHide () {
      this.isVisible = false
    },

    onToggle () {
      this.isVisible ? this.onHide() : this.onShow()
    },

    onDocumentClick (event) {
      if (this.isVisible && this.isInWhiteList(event.target) === false) {
        this.onHide()
      }
    },

    onDestroy () {
      if (this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = null
      }

      if (this.isAppendedToBody) {
        this.isAppendedToBody = false
        document.body.removeChild(this.tooltip)
      }
    },

    setEventListeners (isActive) {
      const element = this.$slots.reference[0].elm

      const triggers = {
        click: [{ name: 'click', handler: this.onShow, element }, { name: 'click', handler: this.onDocumentClick }],
        toggle: [{ name: 'click', handler: this.onToggle, element }, { name: 'click', handler: this.onDocumentClick }],
        hover: [{ name: 'mouseenter', handler: this.onShow, element }, { name: 'mouseleave', handler: this.onHide, element }, { name: 'click', handler: this.onHide, element }],
        focus: [{ name: 'focus', handler: this.onShow, element }, { name: 'blur', handler: this.onHide, element }]
      }

      triggers[this.trigger].forEach(event => isActive ? this.addEventListener(event) : this.removeEventListener(event))
    },

    isInWhiteList (element) {
      return [this.$el, ...this.$el.querySelectorAll('*'), this.tooltip, ...this.tooltip.querySelectorAll('*')].includes(element)
    }
  }

}
</script>
